import { useToast } from "@smakecloud/smake-ui";
import type {
    VectorizeTask,
    VectorizeTaskList,
    VectorizeTaskMeta,
    VectorizeTaskParameters,
    VectorizeTaskResult,
} from "~/lib/api/types/vectorizeTasks";
import {
    cancelVectorizeTask,
    deleteVectorizeTask,
    enqueueVectorizeTask,
    fetchVectorizeTaskFile,
    fetchVectorizeTaskResult,
    fetchVectorizeTasks,
    reorderVectorizeTasks,
    retryVectorizeTask,
    type VectorizeTaskAuth,
} from "~/lib/api/vectorizeTasks";
import { customerTagOf } from "~/lib/vectorize/customer";

/** Polling while something runs or waits — the progress in the list moves. */
const BUSY_POLL_MS = 2000;
/** Polling while nothing runs, to notice tasks queued from another tab. */
const IDLE_POLL_MS = 20_000;
/** Polling while the tab is in the background. */
const HIDDEN_POLL_MS = 30_000;

/**
 * The signed-in user's vectorization queue: one running task, the waiting
 * ones in order, and the finished ones. The tasks run on the image-processor
 * — this store only mirrors them, so leaving a page, switching logos or
 * reloading never touches a task.
 *
 * The list is polled (faster while something is busy); results and
 * thumbnails are loaded once per task and kept.
 */
export const useVectorizeTaskStore = defineStore("vectorizeTaskStore", () => {
    const { token } = storeToRefs(useTokenStore());
    const { slug } = storeToRefs(useSlugStore());
    const { t } = useI18n();
    const { showErrorToast, showToast } = useToast();
    const visibility = useDocumentVisibility();

    const list = ref<VectorizeTaskList>();
    const lastError = ref<string>();
    /** The task the live dialog shows, if it is open. */
    const watchedTaskId = ref<string>();
    const panelOpen = ref<boolean>(false);
    const results = reactive(new Map<string, VectorizeTaskResult>());
    // Tasks a view shows live right now (e.g. the vectorize modal): their
    // end is visible there, so no toast announces it.
    const shownTaskIds = new Set<string>();
    const thumbnails = reactive(new Map<string, string>());

    const pendingResults = new Map<string, Promise<VectorizeTaskResult>>();
    const pendingThumbnails = new Set<string>();
    let reopenPanel = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let refreshing: Promise<void> | undefined;

    const auth = computed<VectorizeTaskAuth | undefined>(() =>
        token.value && slug.value
            ? { slug: slug.value, token: token.value }
            : undefined,
    );

    /** The list was fetched at least once. */
    const loaded = computed(() => list.value !== undefined);
    const active = computed(() => list.value?.active);
    const queued = computed(() => list.value?.queued ?? []);
    const finished = computed(() => list.value?.finished ?? []);
    const tasks = computed<VectorizeTask[]>(() => [
        ...(active.value ? [active.value] : []),
        ...queued.value,
        ...finished.value,
    ]);
    /** Running plus waiting — what the header badge counts. */
    const pendingCount = computed(
        () => queued.value.length + (active.value ? 1 : 0),
    );

    function requireAuth(): VectorizeTaskAuth {
        if (!auth.value) throw new Error("Not signed in");

        return auth.value;
    }

    function taskById(id: string | undefined): VectorizeTask | undefined {
        return id ? tasks.value.find((task) => task.id === id) : undefined;
    }

    function tasksForLogo(logoId: number | undefined): VectorizeTask[] {
        return tasks.value.filter((task) =>
            logoId === undefined
                ? task.meta.origin === "vectorize-page"
                : task.meta.logoId === logoId,
        );
    }

    async function refresh(): Promise<void> {
        if (!auth.value) return;

        refreshing ??= (async () => {
            try {
                const next = await fetchVectorizeTasks(requireAuth());

                announceFinished(list.value, next);
                list.value = next;
                lastError.value = undefined;
            } catch (error) {
                lastError.value =
                    error instanceof Error ? error.message : String(error);
            } finally {
                refreshing = undefined;
            }
        })();

        await refreshing;
    }

    async function enqueue(
        file: File,
        parameters: VectorizeTaskParameters,
        meta: VectorizeTaskMeta,
    ): Promise<VectorizeTask> {
        const task = await enqueueVectorizeTask(
            requireAuth(),
            file,
            parameters,
            meta,
        );

        await refresh();
        schedule();

        return task;
    }

    /**
     * Stops the running task. The list shows it as cancelled at once; the
     * next waiting task starts on its own.
     */
    async function cancel(id: string): Promise<void> {
        if (list.value?.active?.id === id) {
            const { active, ...rest } = list.value;

            list.value = {
                ...rest,
                finished: [
                    {
                        ...active,
                        finishedAt: Date.now(),
                        progress: undefined,
                        status: "cancelled",
                    },
                    ...rest.finished,
                ],
            };
        }

        try {
            await cancelVectorizeTask(requireAuth(), id);
        } catch (error) {
            showErrorToast(
                error instanceof Error ? error.message : String(error),
            );
        }

        await refresh();
        schedule();
    }

    /** Drops a waiting task or clears a finished one from the list. */
    async function remove(id: string): Promise<void> {
        try {
            await deleteVectorizeTask(requireAuth(), id);
        } catch (error) {
            showErrorToast(
                error instanceof Error ? error.message : String(error),
            );
        }

        forget(id);
        await refresh();
    }

    /**
     * Clears a logo's finished tasks from the list — its SVG is in use now.
     * They go at once; one the API keeps comes back with the next poll.
     * Running and waiting tasks stay.
     */
    async function dismissLogo(logoId: number): Promise<void> {
        const ids = new Set(
            finished.value
                .filter((task) => task.meta.logoId === logoId)
                .map((task) => task.id),
        );

        if (ids.size === 0 || !list.value) return;

        list.value = {
            ...list.value,
            finished: list.value.finished.filter((task) => !ids.has(task.id)),
        };

        const auth = requireAuth();

        await Promise.allSettled(
            [...ids].map((id) => deleteVectorizeTask(auth, id)),
        );

        for (const id of ids) forget(id);
        await refresh();
    }

    function forget(id: string): void {
        const url = thumbnails.get(id);

        if (url) URL.revokeObjectURL(url);
        thumbnails.delete(id);
        results.delete(id);
    }

    /** New order of the waiting tasks — shown at once, confirmed by the API. */
    async function reorder(ids: string[]): Promise<void> {
        if (list.value) {
            const byId = new Map(
                list.value.queued.map((task) => [task.id, task]),
            );

            list.value = {
                ...list.value,
                queued: ids.flatMap((id, position) => {
                    const task = byId.get(id);

                    return task ? [{ ...task, position }] : [];
                }),
            };
        }

        try {
            list.value = await reorderVectorizeTasks(requireAuth(), ids);
        } catch (error) {
            showErrorToast(
                error instanceof Error ? error.message : String(error),
            );
            await refresh();
        }
    }

    /** Moves a waiting task by `offset` places (negative: earlier). */
    async function move(id: string, offset: number): Promise<void> {
        const ids = queued.value.map((task) => task.id);
        const from = ids.indexOf(id);
        const to = Math.max(0, Math.min(ids.length - 1, from + offset));

        if (from === -1 || from === to) return;

        ids.splice(from, 1);
        ids.splice(to, 0, id);
        await reorder(ids);
    }

    async function retry(id: string): Promise<void> {
        try {
            await retryVectorizeTask(requireAuth(), id);
        } catch (error) {
            showErrorToast(
                error instanceof Error ? error.message : String(error),
            );
        }

        await refresh();
        schedule();
    }

    /** The result of a finished task, loaded once. */
    async function loadResult(id: string): Promise<VectorizeTaskResult> {
        const known = results.get(id);

        if (known) return known;

        let pending = pendingResults.get(id);

        if (!pending) {
            pending = fetchVectorizeTaskResult(requireAuth(), id).finally(
                () => {
                    pendingResults.delete(id);
                },
            );
            pendingResults.set(id, pending);
        }

        const result = await pending;

        results.set(id, result);

        return result;
    }

    async function loadSource(id: string): Promise<Blob> {
        return fetchVectorizeTaskFile(requireAuth(), id, "source");
    }

    /** Starts loading a task's thumbnail; `thumbnails` has it once it is there. */
    function ensureThumbnail(task: VectorizeTask): void {
        if (
            !task.hasThumbnail ||
            thumbnails.has(task.id) ||
            pendingThumbnails.has(task.id) ||
            !auth.value
        ) {
            return;
        }

        pendingThumbnails.add(task.id);
        fetchVectorizeTaskFile(auth.value, task.id, "thumbnail")
            .then((blob) => {
                thumbnails.set(task.id, URL.createObjectURL(blob));
            })
            .catch(() => {
                // No thumbnail: the list shows a placeholder.
            })
            .finally(() => {
                pendingThumbnails.delete(task.id);
            });
    }

    /**
     * Opens the live dialog on a task. The panel makes way meanwhile — as a
     * modal drawer it turns pointer events off everywhere else, the dialog on
     * top included — and comes back once the dialog closes.
     */
    function openLive(id: string): void {
        reopenPanel ||= panelOpen.value;
        panelOpen.value = false;
        watchedTaskId.value = id;
        schedule();
    }

    function closeLive(): void {
        watchedTaskId.value = undefined;

        if (reopenPanel) panelOpen.value = true;

        reopenPanel = false;
    }

    /** A view shows this task live (true) or no longer does (false). */
    function setShown(id: string, shown: boolean): void {
        if (shown) {
            shownTaskIds.add(id);
        } else {
            shownTaskIds.delete(id);
        }
    }

    /**
     * Tells the user when a task of theirs ended while they were elsewhere;
     * the queue drawer leads on to the result. Every task that ran or waited before and is
     * finished now counts: a quick (cached) one can start and end between two
     * polls. A cancelled task is not announced, the user stopped it.
     */
    function announceFinished(
        previous: VectorizeTaskList | undefined,
        next: VectorizeTaskList,
    ): void {
        if (!previous) return;

        const pending = new Set(
            [
                ...(previous.active ? [previous.active] : []),
                ...previous.queued,
            ].map((task) => task.id),
        );

        for (const ended of next.finished) {
            if (
                !pending.has(ended.id) ||
                ended.status === "cancelled" ||
                watchedTaskId.value === ended.id ||
                shownTaskIds.has(ended.id)
            ) {
                continue;
            }

            const name = taskLabel(ended);
            const done = ended.status === "done";

            showToast(
                done
                    ? t("vectorize.queue.finished_toast", { name })
                    : t("vectorize.queue.failed_toast", { name }),
                {
                    duration: 8000,
                    status: done ? "success" : "error",
                },
            );
        }
    }

    /**
     * What a task is called in the queue: its customer — number and, where
     * known, country — to tell it apart, and its logo or file name.
     */
    function taskLabel(task: VectorizeTask): string {
        const name =
            task.meta.logoName ??
            task.meta.fileName ??
            t("vectorize.queue.untitled");
        const customer = customerTagOf(task.meta);

        if (!customer) return name;

        return [
            t("vectorize.queue.customer_number", { number: customer.number }),
            customer.country,
            name,
        ]
            .filter((part) => part !== undefined)
            .join(" · ");
    }

    function schedule(): void {
        clearTimeout(timer);

        if (!auth.value) return;

        let delay: number;

        if (visibility.value === "hidden") {
            delay = HIDDEN_POLL_MS;
        } else if (pendingCount.value > 0 || watchedTaskId.value) {
            delay = BUSY_POLL_MS;
        } else {
            delay = IDLE_POLL_MS;
        }

        timer = setTimeout(() => {
            void refresh().finally(schedule);
        }, delay);
    }

    // Signed in (or another app): start over with that user's queue.
    watch(
        auth,
        (value, old) => {
            if (value?.slug !== old?.slug || !value) {
                list.value = undefined;
                results.clear();

                for (const url of thumbnails.values()) URL.revokeObjectURL(url);
                thumbnails.clear();
            }

            if (value) {
                void refresh().finally(schedule);
            } else {
                clearTimeout(timer);
            }
        },
        { immediate: true },
    );

    watch(visibility, (value) => {
        if (value === "visible") void refresh().finally(schedule);
    });

    return {
        active,
        cancel,
        closeLive,
        dismissLogo,
        enqueue,
        ensureThumbnail,
        finished,
        lastError,
        loaded,
        loadResult,
        loadSource,
        move,
        openLive,
        panelOpen,
        pendingCount,
        queued,
        refresh,
        remove,
        reorder,
        results,
        retry,
        setShown,
        taskById,
        taskLabel,
        tasks,
        tasksForLogo,
        thumbnails,
        watchedTaskId,
    };
});
